import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import { apiClient } from "../api/client";
import { AxiosRequestConfig } from "axios";
import { clearAuth0Storage } from "../utils/auth-utils";

// Global flag to prevent multiple simultaneous redirects across all hook instances
// This is module-level, not instance-level, to coordinate across all components
let globalRedirecting = false;
let redirectTimeout: ReturnType<typeof setTimeout> | null = null;

// Export function to check if a redirect is already in progress
// This allows other components (like SmartProtectedApp) to coordinate redirects
export const isRedirecting = () => globalRedirecting;

export const useAuthenticatedApi = () => {
  const { getAccessTokenSilently, loginWithRedirect, logout } = useAuth0();

  // This function wraps our standard apiClient to add the auth token.
  const callApi = useCallback(
    async (config: AxiosRequestConfig) => {
      try {
        if (import.meta.env.DEV) console.log("callApi: getting access token");
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only", // Use cache first, then fallback to refresh
          timeoutInSeconds: 10, // Add timeout to prevent hanging
        });
        if (import.meta.env.DEV)
          console.log("callApi: requesting:", config.url);

        try {
          const response = await apiClient.request({
            ...config,
            headers: {
              ...config.headers,
              Authorization: `Bearer ${token}`,
            },
          });
          if (import.meta.env.DEV) console.log("callApi: request successful");
          return response;
        } catch (apiError: any) {
          if (import.meta.env.DEV)
            console.error("callApi: request failed:", apiError);
          // If backend says unauthorized (expired/invalid token), trigger re-login
          // Use global flag to prevent multiple redirects across all hook instances
          // Check for employee access revoked error (403 ForbiddenError with specific message)
          const isEmployeeAccessRevoked =
            apiError?.response?.status === 403 &&
            apiError?.response?.data?.error?.type === "ForbiddenError" &&
            (apiError?.response?.data?.error?.debugInfo?.stackSummary?.includes(
              "Employee access denied"
            ) ||
              apiError?.response?.data?.error?.debugInfo?.stackSummary?.includes(
                "No associated employee profile found"
              ) ||
              apiError?.response?.data?.error?.message?.includes(
                "do not have permission"
              ));

          if (isEmployeeAccessRevoked && !globalRedirecting) {
            if (import.meta.env.DEV)
              console.warn(
                "Employee access revoked detected – redirecting to access revoked page"
              );

            // Prevent redirect loops
            if (window.location.pathname !== "/employee-access-revoked") {
              globalRedirecting = true;
              window.location.href = "/employee-access-revoked";
            }

            return Promise.reject(
              new Error("Employee access revoked - redirecting")
            );
          }

          if (apiError?.response?.status === 401 && !globalRedirecting) {
            if (import.meta.env.DEV)
              console.warn("Backend returned 401 – redirecting to login");

            // Set global flag immediately to prevent race conditions
            globalRedirecting = true;

            // Clear any existing redirect timeout
            if (redirectTimeout) {
              clearTimeout(redirectTimeout);
            }

            try {
              await logout({
                logoutParams: {
                  returnTo: window.location.origin + window.location.pathname,
                },
                openUrl: false,
              });
            } catch (_) {}

            clearAuth0Storage();

            redirectTimeout = setTimeout(() => {
              sessionStorage.setItem(
                "auth0_return_to",
                window.location.pathname + window.location.search
              );
              loginWithRedirect({
                authorizationParams: {
                  response_type: "code",
                  response_mode: "query",
                  scope: "openid profile email offline_access",
                  prompt: "login",
                },
              });
              
              // Reset global flag after redirect (with delay to prevent immediate re-trigger)
              setTimeout(() => {
                globalRedirecting = false;
                redirectTimeout = null;
              }, 1000);
            }, 300);

            return Promise.reject(
              new Error("Authentication required - redirecting to login")
            );
          }

          throw apiError;
        }
      } catch (error: any) {
        if (import.meta.env.DEV)
          console.error("Authenticated API call error:", error);

        // Check for employee access revoked error in outer catch as well
        const isEmployeeAccessRevoked =
          error?.response?.status === 403 &&
          error?.response?.data?.error?.type === "ForbiddenError" &&
          (error?.response?.data?.error?.debugInfo?.stackSummary?.includes(
            "Employee access denied"
          ) ||
            error?.response?.data?.error?.debugInfo?.stackSummary?.includes(
              "No associated employee profile found"
            ) ||
            error?.response?.data?.error?.message?.includes(
              "do not have permission"
            ));

        if (isEmployeeAccessRevoked && !globalRedirecting) {
          if (import.meta.env.DEV)
            console.warn(
              "Employee access revoked detected – redirecting to access revoked page"
            );

          // Prevent redirect loops
          if (window.location.pathname !== "/employee-access-revoked") {
            globalRedirecting = true;
            window.location.href = "/employee-access-revoked";
          }

          return Promise.reject(
            new Error("Employee access revoked - redirecting")
          );
        }

        // Handle specific Auth0 errors that indicate refresh token issues
        const isRefreshTokenError =
          error?.message?.includes("Missing Refresh Token") ||
          error?.message?.includes("refresh_token") ||
          error?.message?.includes("Unknown or invalid refresh token") ||
          error?.error === "login_required" ||
          error?.error === "invalid_grant" ||
          error?.error_description?.includes("refresh_token");

        // Use global flag to prevent multiple redirects across all hook instances
        if (isRefreshTokenError && !globalRedirecting) {
          if (import.meta.env.DEV)
            console.warn(
              "Refresh token issue detected, clearing Auth0 cache and re-login...",
              error
            );

          // Set global flag immediately to prevent race conditions
          globalRedirecting = true;

          // Clear any existing redirect timeout
          if (redirectTimeout) {
            clearTimeout(redirectTimeout);
          }

          try {
            // Clear Auth0 cache specifically instead of all localStorage
            await logout({
              logoutParams: {
                returnTo: window.location.origin + window.location.pathname,
              },
              openUrl: false, // Don't open logout URL, just clear local session
            });
          } catch (logoutError) {
            if (import.meta.env.DEV)
              console.warn("Failed to logout cleanly:", logoutError);
          }

          // Clear Auth0 related localStorage items specifically
          clearAuth0Storage();

          // Add a small delay before redirect to prevent rapid redirects
          redirectTimeout = setTimeout(() => {
            // Store the current path before redirecting
            sessionStorage.setItem(
              "auth0_return_to",
              window.location.pathname + window.location.search
            );
            loginWithRedirect({
              authorizationParams: {
                response_type: "code",
                response_mode: "query",
                scope: "openid profile email offline_access",
                prompt: "login", // Force re-authentication
              },
            });
            
            // Reset global flag after redirect (with delay to prevent immediate re-trigger)
            setTimeout(() => {
              globalRedirecting = false;
              redirectTimeout = null;
            }, 1000);
          }, 500);

          return Promise.reject(
            new Error("Authentication required - redirecting to login")
          );
        }

        // If the cache-only approach failed, try with a fresh token
        if (
          error?.error === "cache_miss" ||
          error?.message?.includes("cache")
        ) {
          try {
            const token = await getAccessTokenSilently({
              cacheMode: "off", // Force fresh token
              timeoutInSeconds: 10,
            });

            return apiClient.request({
              ...config,
              headers: {
                ...config.headers,
                Authorization: `Bearer ${token}`,
              },
            });
          } catch (freshTokenError) {
            if (import.meta.env.DEV)
              console.error("Failed to get fresh token:", freshTokenError);
            throw freshTokenError;
          }
        }

        // For other errors, just re-throw
        throw error;
      }
    },
    [getAccessTokenSilently, loginWithRedirect, logout]
  );

  return { callApi };
};
